// Tracks an in-flight MSAL loginRedirect so the shell does not Redirect to
// /login while the hash is empty and getAllAccounts() is not yet populated.
// Cleared on success/failure/finalize, and when the user abandons B2C (browser
// Back / bfcache) via clearAbandonedInteractiveAuth — keep interaction_in_progress
// guards for true concurrent MSAL interactive APIs.

const KEY = "ads.auth.pendingInteractiveLogin";
const MAX_MS = 5 * 60 * 1000;

export const setAuthRedirectPending = () => {
  try {
    sessionStorage.setItem(KEY, String(Date.now()));
  } catch (_e) {
    // ignore
  }
};

export const clearAuthRedirectPending = () => {
  try {
    sessionStorage.removeItem(KEY);
  } catch (_e) {
    // ignore
  }
};

export const isAuthRedirectPending = () => {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return false;
    const t = Number(raw);
    if (!Number.isFinite(t)) {
      return raw === "1";
    }
    if (Date.now() - t > MAX_MS) {
      sessionStorage.removeItem(KEY);
      return false;
    }
    return true;
  } catch (_e) {
    return false;
  }
};

/** True when the URL looks like an OAuth / MSAL redirect response in progress. */
export const isMsalAuthHandoffInUrl = (
  href = typeof window !== "undefined" ? window.location.href : "",
  hash = typeof window !== "undefined" ? window.location.hash : "",
) => {
  try {
    const blob = `${href || ""} ${hash || ""}`;
    return /[?#&](code|error|error_description|client_info|session_state)=/i.test(
      blob,
    );
  } catch (_e) {
    return false;
  }
};

/** Remove stale MSAL temp interaction/request keys (local + session). */
export const clearStaleMsalInteractionCache = () => {
  const scrub = (storage) => {
    try {
      Object.keys(storage)
        .filter(
          (key) =>
            key.startsWith("msal.") &&
            (key.includes("interaction") || key.includes("request")),
        )
        .forEach((key) => storage.removeItem(key));
    } catch (_e) {
      // ignore
    }
  };
  if (typeof localStorage !== "undefined") {
    scrub(localStorage);
  }
  if (typeof sessionStorage !== "undefined") {
    scrub(sessionStorage);
  }
};

/**
 * Clear app-owned interactive-login locks after abandoned B2C redirect.
 * Does not disable MSAL interaction_in_progress retries for real overlap.
 */
export const clearAbandonedInteractiveAuth = () => {
  clearAuthRedirectPending();
  clearStaleMsalInteractionCache();
};

/**
 * Whether to recover Login from abandoned interactive auth.
 * Never clears during live handleRedirect or OAuth handoff URLs.
 */
export const shouldClearAbandonedInteractiveAuth = ({
  msalInProgress,
  hasLocalAuthInProgress = false,
  fromPersistedPageshow = false,
  href,
  hash,
} = {}) => {
  if (msalInProgress === "handleRedirect") {
    return false;
  }
  if (isMsalAuthHandoffInUrl(href, hash)) {
    return false;
  }
  if (isAuthRedirectPending()) {
    return true;
  }
  // bfcache restore can leave Login.authInProgress true with no pending flag
  return Boolean(fromPersistedPageshow && hasLocalAuthInProgress);
};
